<template>
  <div :class="elementClass">
    <div :class="elementInner" ref="inner">
      <input
        @focus="focus"
        @keydown="onKeyDown($event)"
        @input="onKeyUp($event)"
        :required="required"
        ref="input"
        :type="type === 'number' ? 'text' : type"
        :value="tempValue"
        autocomplete="new-password"
        :disabled="disabled || label"
        @blur="blur"
      />
      <div class="floating-placeholder" @click="focusInput">{{ placeholder }} <span class="color-red" v-if="required">*</span></div>
    </div>
    <div v-if="!noMessage" :class="messageClass">{{ message }}</div>
  </div>
</template>

<script>
import { textfieldClass } from "../prefix";
export default {
  props: {
    type: {
      default: "",
      type: String,
    },
    disabled: {
      default: false,
      type: Boolean,
    },
    value: {
      default: "",
      type: String,
    },
    placeholder: {
      default: "",
      type: String,
    },
    message: {
      default: "",
      type: String,
    },
    noMessage: {
      default: false,
      type: Boolean,
    },
    required: {
      default: false,
      type: Boolean,
    },
    label: {
      default: false,
      type: Boolean,
    },
    noLine: {
      default: false,
      type: Boolean,
    },
  },
  data() {
    return {
      isFocus: false,
      tempValue: this.value,
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.$emit("mounted", this.$refs.inner);
    });
  },
  computed: {
    elementClass() {
      return textfieldClass;
    },
    elementInner() {
      return (
        "inner" +
        (this.isFocus ? " focus" : "") +
        (this.value || this.isFocus ? " float" : "") +
        (this.disabled ? " disabled" : "") +
        (this.label ? " label" : "")
      );
    },
    messageClass() {
      return "message" + (this.isFocus ? " focus" : "");
    },
  },
  methods: {
    blur() {
      this.isFocus = false;
      this.$emit("blur");
    },
    makeBlur() {
      this.$refs.input.blur();
    },
    updateValue(value) {
      this.tempValue = value;
      this.$emit("input", value);
    },
    focus() {
      this.isFocus = true;
      this.$emit("focus", this.$refs.inner);
    },
    focusInput() {
      this.$refs.input.focus();
      this.focus();
    },
    onKeyDown(e) {
      if (this.type === "number" && !this.checkingisPossibleNumber(e)) {
        e.preventDefault();
      }
    },
    onKeyUp(e) {
      if (this.type === "number") {
        let minNumber = 0;
        let maxNumber = 100;
        if (e.keyCode === 38 && parseFloat(e.target.value) < maxNumber) {
          return this.updateValue((parseFloat(e.target.value) + 1).toString());
        }
        if (e.keyCode === 40 && parseFloat(e.target.value) > minNumber) {
          return this.updateValue((parseFloat(e.target.value) - 1).toString());
        }

        if (this.checkingisPossibleNumber(e)) {
          return this.updateValue(e.target.value);
        }
      } else {
        return this.updateValue(e.target.value);
      }
    },
    checkingisPossibleNumber(e) {
      let notContainDot = !this.tempValue.includes(".");
      let indexOfDot = !notContainDot && this.tempValue.indexOf(".");
      let maxDecimalPoint = 2;
      if (this.type !== "number") {
        return true;
      }
      if (e.keyCode === 8 || e.keyCode === 37 || e.keyCode === 39) {
        return true;
      }

      if (indexOfDot && this.tempValue.length > maxDecimalPoint + indexOfDot) {
        return false;
      }
      return (e.keyCode >= 48 && e.keyCode <= 57) || (e.keyCode === 190 && notContainDot) || (e.keyCode >= 96 && e.keyCode <= 105);
    },
    getInner() {
      return this.$refs.inner;
    },
  },
  watch: {
    value(val) {
      this.tempValue = val;
    },
  },
};
</script>

<style lang="scss">
@import "../scss/import.scss";
$element: $prefix + "-textfield";

#{$element} {
  padding-bottom: 4px;
  .inner {
    z-index: 2;
    // background: $color-grey-50;
    background: $color-white;
    padding: 10px 16px;
    // overflow: hidden;
    // border-top-left-radius: 4px;
    // border-top-right-radius: 4px;
    border-radius: 4px;
    cursor: text;
    border: 1px solid $color-grey-300;
    input {
      width: 100%;
      border: 0;
      margin: 0;
      box-shadow: none;
      font-size: 12px;
      line-height: 24px;
      height: 24px;
      outline: none;
      background: transparent;
      letter-spacing: -0.09px;
      @include transition(all 0.15s $animation-deceleration-curve-timing-function);
      z-index: 2;
    }

    .floating-placeholder {
      position: absolute;
      top: 12px;
      left: 16px;
      width: 100%;
      height: 100%;
      font-size: 12px;
      line-height: 20px;
      color: $color-grey-600;
      font-weight: 500;
      @include transition(all 0.15s $animation-acceleration-curve-timing-function);
      @include transform-origin((0));
      @include unselectable();
      z-index: 1;
    }

    .backline {
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 1px;
    }

    &:hover {
      .floating-placeholder {
        color: $color-grey-600;
      }
    }

    &.float {
      .floating-placeholder {
        color: $color-grey-500;
        @include transform(translateY(-13px) scale(0.9));
      }

      input {
        @include transform(translateY(6px));
      }
    }

    &.focus {
      border: 1px solid $theme-color;
      .floating-placeholder {
        color: $theme-primary;
      }
    }

    &.disabled {
      pointer-events: none;
      cursor: none;

      input {
        color: $color-grey-500;
      }

      .floating-placeholder {
        color: $color-grey-400;
      }
    }

    &.label {
      pointer-events: none;
      cursor: none;

      input {
        color: $color-grey-800;
      }

      .floating-placeholder {
        color: $color-grey-700;
      }
    }
  }

  .message {
    z-index: 1;
    font-size: 10px;
    color: $color-grey-500;
    line-height: 16px;
    height: 16px;
    padding: 0 16px;
    @include transform(translateY(-10px));
    opacity: 0;
    &.focus {
      color: $theme-primary;
      @include transform(translateY(0px));
      @include transition(all 0.2s $animation-sharp-curve-timing-function);
      opacity: 1;
    }
  }
}
</style>
